import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const I18nContext = createContext(null);

const DEFAULT_LANG = "tr";
const STORAGE_KEY = "firat_lang";
const SUPPORTED = ["tr", "en", "ar"];

// Auto-detect site language from the visitor's phone/browser locale.
// tr → Turkish, ar → Arabic, everything else → English (best for intl. Google Ads traffic).
const detectBrowserLang = () => {
  try {
    if (typeof navigator === "undefined") return DEFAULT_LANG;
    const langs =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || navigator.userLanguage || ""];
    for (const l of langs) {
      const code = (l || "").toLowerCase().split("-")[0];
      if (code === "tr") return "tr";
      if (code === "ar") return "ar";
      if (code === "en") return "en";
    }
    return "en";
  } catch {
    return DEFAULT_LANG;
  }
};

// Base UI strings. Content strings come from backend as Localized objects.
export const STRINGS = {
  tr: {
    nav: {
      home: "Ana Sayfa",
      about: "Hakkımızda",
      businesses: "Faaliyet Alanları",
      companies: "Şirketlerimiz",
      projects: "İnşaat",
      media: "Medya",
      careers: "Kariyer",
      contact: "İletişim",
      admin: "Yönetim",
    },
    common: {
      readMore: "Devamını Oku",
      viewProject: "Projeyi İncele",
      viewAll: "Tümünü Gör",
      applyNow: "Başvuruyu Gönder",
      send: "Gönder",
      loading: "Yükleniyor…",
      required: "Bu alan zorunludur",
      language: "Dil",
      search: "Ara",
      copyright: "Tüm hakları saklıdır.",
      chairmanTitle: "Yönetim Kurulu Başkanı'nın Mesajı",
      backHome: "Ana Sayfaya Dön",
      submitted: "Mesajınız başarıyla iletildi.",
      applyReceived: "Başvurunuz alındı. Teşekkür ederiz.",
      allCategories: "Tüm Kategoriler",
      allStatuses: "Tüm Durumlar",
      projects: "Projeler",
      completed: "Tamamlandı",
      ongoing: "Devam Ediyor",
      upcoming: "Yakında",
      residential: "Konut",
      commercial: "Ticari",
      infrastructure: "Altyapı",
      hospitality: "Otelcilik",
      "mixed-use": "Karma Kullanım",
      industrial: "Endüstriyel",
      news: "Haber",
      press: "Basın Bülteni",
      publication: "Yayın",
    },
    construction: {
      heroEyebrow: "İnşaat",
      heroTitle: "Konut, ticari, otel ve kamu projelerinde uçtan uca inşaat.",
      heroSubtitle: "Kavramsal tasarımdan anahtar teslim uygulamaya; mühendislik, disiplin ve zamanında teslim.",
      whatWeDo: "İnşaat Sektöründe Neler Yapıyoruz?",
      whatWeDoIntro: "Arsa geliştirmeden anahtar teslim yapıya kadar tüm süreci tek elden yönetiyoruz.",
      services: {
        residential: {
          title: "Konut Projeleri",
          body: "Rezidans, villa, site ve toplu konut projelerinde plan, proje, ruhsat, uygulama ve iskân süreçlerini yönetiyoruz.",
        },
        commercial: {
          title: "Ticari Projeler",
          body: "Ofis, plaza, iş merkezi ve karma kullanım yapılarını yatırımcı gözüyle tasarlıyor ve inşa ediyoruz.",
        },
        hospitality: {
          title: "Otel Projeleri",
          body: "Butik konseptten uluslararası zincirlere; operasyonel verimlilik odaklı otel yapıları geliştiriyoruz.",
        },
        public: {
          title: "Kamu Projeleri",
          body: "Eğitim, sağlık, yönetim binaları ve altyapı işlerinde kamu ihale standartlarında teslimat yapıyoruz.",
        },
      },
      landDeal: {
        eyebrow: "Kat Karşılığı İnşaat",
        title: "Arsanıza değerini kazandıralım.",
        body: "İstanbul ve çevresindeki arsalarınız için kat karşılığı inşaat modeliyle sıfır maliyetli değer üretiyoruz. Uzman mimar, mühendis ve saha ekibimizle A'dan Z'ye tek muhatap firma.",
        benefits: [
          "Şeffaf sözleşme ve noter onayı",
          "Kaliteden ödün vermeyen ısrarcı ekip",
          "Zamanında ve eksiksiz teslim taahhüdü",
          "Yapı denetim ve iskân sürecinin tam takibi",
          "Pazarlama ve satış desteği (isteğe bağlı)",
        ],
        cta: "Arsanız İçin Ön Görüşme Talep Edin",
      },
      referenceProjects: "Referans Projelerimiz",
      referenceIntro: "Türkiye ve yurt dışında tamamladığımız ve devam eden inşaatlar.",
      contactCta: "Projeniz İçin Görüşelim",
    },
    home: {
      exploreProjects: "Projelerimizi Keşfedin",
      globalPresence: "Küresel Varlık",
      globalPresenceIntro: "Üç kıtada, iz bırakan projeler.",
      featuredProjects: "Öne Çıkan Projeler",
      mediaCenter: "Medya Merkezi",
      businesses: "Faaliyet Alanlarımız",
      partners: "İş Ortaklarımız",
      careersCta: "Ekibimize Katılın",
      careersCtaBody: "Sınırların ötesinde projelere imza atmak isteyen liderleri arıyoruz.",
      contactCta: "Bize Ulaşın",
    },
    forms: {
      name: "Adınız Soyadınız",
      email: "E-posta",
      phone: "Telefon",
      subject: "Konu",
      department: "Departman",
      message: "Mesajınız",
      position: "Pozisyon",
      resumeUrl: "Özgeçmiş bağlantısı (URL)",
    },
    login: {
      title: "Yönetim Paneli Girişi",
      submit: "Giriş Yap",
      invalid: "Geçersiz e-posta veya şifre",
    },
  },
  en: {
    nav: {
      home: "Home",
      about: "About",
      businesses: "Businesses",
      companies: "Companies",
      projects: "Construction",
      media: "Media",
      careers: "Careers",
      contact: "Contact",
      admin: "Admin",
    },
    common: {
      readMore: "Read More",
      viewProject: "View Project",
      viewAll: "View All",
      applyNow: "Submit Application",
      send: "Send",
      loading: "Loading…",
      required: "This field is required",
      language: "Language",
      search: "Search",
      copyright: "All rights reserved.",
      chairmanTitle: "Chairman's Message",
      backHome: "Back to Home",
      submitted: "Your message has been sent.",
      applyReceived: "Your application has been received. Thank you.",
      allCategories: "All Categories",
      allStatuses: "All Statuses",
      projects: "Projects",
      completed: "Completed",
      ongoing: "Ongoing",
      upcoming: "Upcoming",
      residential: "Residential",
      commercial: "Commercial",
      infrastructure: "Infrastructure",
      hospitality: "Hospitality",
      "mixed-use": "Mixed-Use",
      industrial: "Industrial",
      news: "News",
      press: "Press Release",
      publication: "Publication",
    },
    construction: {
      heroEyebrow: "Construction",
      heroTitle: "End-to-end construction across residential, commercial, hospitality and public projects.",
      heroSubtitle: "From concept design to turnkey delivery — engineering, discipline, on-time.",
      whatWeDo: "What We Do in Construction",
      whatWeDoIntro: "From land development to turnkey buildings, we manage the entire process under one roof.",
      services: {
        residential: {
          title: "Residential",
          body: "Residences, villas, gated communities and mass housing — planning, permits, execution and occupancy.",
        },
        commercial: {
          title: "Commercial",
          body: "Offices, plazas, business centers and mixed-use developments designed for investor value.",
        },
        hospitality: {
          title: "Hospitality",
          body: "From boutique to international chains — hotels engineered for operational efficiency.",
        },
        public: {
          title: "Public",
          body: "Schools, hospitals, government buildings and infrastructure delivered to public-tender standards.",
        },
      },
      landDeal: {
        eyebrow: "Revenue-Sharing (Kat Karşılığı)",
        title: "Unlock the value of your land.",
        body: "We develop your land in Istanbul and beyond under a revenue-sharing model — zero upfront cost. A single partner from design to sales.",
        benefits: [
          "Transparent, notarized agreements",
          "Uncompromising quality standards",
          "On-time & complete delivery",
          "Full building-inspection & occupancy tracking",
          "Optional sales & marketing support",
        ],
        cta: "Request a Land Consultation",
      },
      referenceProjects: "Reference Projects",
      referenceIntro: "Landmark projects we've delivered and are building — locally and internationally.",
      contactCta: "Let's Talk About Your Project",
    },
    home: {
      exploreProjects: "Explore Our Projects",
      globalPresence: "Global Presence",
      globalPresenceIntro: "Landmark projects on three continents.",
      featuredProjects: "Featured Projects",
      mediaCenter: "Media Center",
      businesses: "Our Businesses",
      partners: "Our Partners",
      careersCta: "Join Our Team",
      careersCtaBody: "We are looking for leaders eager to shape projects beyond borders.",
      contactCta: "Contact Us",
    },
    forms: {
      name: "Full Name",
      email: "Email",
      phone: "Phone",
      subject: "Subject",
      department: "Department",
      message: "Your Message",
      position: "Position",
      resumeUrl: "Resume URL",
    },
    login: {
      title: "Admin Panel Login",
      submit: "Sign In",
      invalid: "Invalid email or password",
    },
  },
  ar: {
    nav: {
      home: "الرئيسية",
      about: "من نحن",
      businesses: "قطاعات الأعمال",
      companies: "الشركات",
      projects: "الإنشاء",
      media: "الإعلام",
      careers: "الوظائف",
      contact: "اتصل بنا",
      admin: "الإدارة",
    },
    common: {
      readMore: "اقرأ المزيد",
      viewProject: "عرض المشروع",
      viewAll: "عرض الكل",
      applyNow: "قدّم الآن",
      send: "إرسال",
      loading: "جارٍ التحميل…",
      required: "هذا الحقل مطلوب",
      language: "اللغة",
      search: "بحث",
      copyright: "جميع الحقوق محفوظة.",
      chairmanTitle: "كلمة رئيس مجلس الإدارة",
      backHome: "العودة للرئيسية",
      submitted: "تم إرسال رسالتك بنجاح.",
      applyReceived: "تم استلام طلبك. شكراً لك.",
      allCategories: "كل التصنيفات",
      allStatuses: "كل الحالات",
      projects: "المشاريع",
      completed: "منجز",
      ongoing: "جارٍ التنفيذ",
      upcoming: "قادم",
      residential: "سكني",
      commercial: "تجاري",
      infrastructure: "بنية تحتية",
      hospitality: "ضيافة",
      "mixed-use": "متعدد الاستخدامات",
      industrial: "صناعي",
      news: "أخبار",
      press: "بيان صحفي",
      publication: "منشور",
    },
    construction: {
      heroEyebrow: "الإنشاء",
      heroTitle: "إنشاء متكامل للمشاريع السكنية والتجارية والفندقية والحكومية.",
      heroSubtitle: "من التصميم المفاهيمي إلى التسليم الجاهز — هندسة والتزام وتسليم في الوقت المحدد.",
      whatWeDo: "ماذا نُنجز في قطاع الإنشاء؟",
      whatWeDoIntro: "من تطوير الأرض إلى التسليم الجاهز؛ نُدير كامل العملية تحت سقف واحد.",
      services: {
        residential: {
          title: "المشاريع السكنية",
          body: "مساكن وفلل ومجمعات — تخطيط وتراخيص وتنفيذ وتسليم.",
        },
        commercial: {
          title: "المشاريع التجارية",
          body: "مكاتب وأبراج ومراكز أعمال ومشاريع مختلطة مصممة لقيمة المستثمر.",
        },
        hospitality: {
          title: "المشاريع الفندقية",
          body: "من الفنادق البوتيك إلى السلاسل العالمية — مصممة للكفاءة التشغيلية.",
        },
        public: {
          title: "المشاريع الحكومية",
          body: "مدارس ومستشفيات ومبانٍ حكومية بمعايير المناقصات العامة.",
        },
      },
      landDeal: {
        eyebrow: "المقاسمة بالطوابق (كات كارشيلي)",
        title: "لنطلق قيمة أرضك.",
        body: "نطوّر أرضك في إسطنبول ومحيطها وفق نموذج المقاسمة بلا تكلفة مسبقة، شريك واحد من التصميم إلى البيع.",
        benefits: [
          "عقود شفافة موثّقة",
          "معايير جودة عالية بلا تنازلات",
          "تسليم في الوقت المحدد وبشكل كامل",
          "متابعة كاملة لتدقيق البناء والإشغال",
          "دعم اختياري للتسويق والمبيعات",
        ],
        cta: "اطلب استشارة الأرض",
      },
      referenceProjects: "مشاريعنا المرجعية",
      referenceIntro: "أعمال إنشائية أنجزناها ونعمل عليها محلياً ودولياً.",
      contactCta: "لنتحدث عن مشروعك",
    },
    home: {
      exploreProjects: "اكتشف مشاريعنا",
      globalPresence: "الحضور الدولي",
      globalPresenceIntro: "مشاريع مميزة في ثلاث قارات.",
      featuredProjects: "مشاريع مميزة",
      mediaCenter: "المركز الإعلامي",
      businesses: "قطاعات أعمالنا",
      partners: "شركاؤنا",
      careersCta: "انضم إلى فريقنا",
      careersCtaBody: "نبحث عن قادة يسعون لتنفيذ مشاريع تتجاوز الحدود.",
      contactCta: "تواصل معنا",
    },
    forms: {
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      subject: "الموضوع",
      department: "القسم",
      message: "رسالتك",
      position: "الوظيفة",
      resumeUrl: "رابط السيرة الذاتية",
    },
    login: {
      title: "دخول لوحة التحكم",
      submit: "تسجيل الدخول",
      invalid: "بريد إلكتروني أو كلمة مرور غير صالحة",
    },
  },
};

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
      // Respect a saved manual choice; otherwise follow the phone/browser language.
      if (stored && SUPPORTED.includes(stored)) return stored;
      return detectBrowserLang();
    } catch {
      return DEFAULT_LANG;
    }
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
      document.documentElement.lang = l;
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  // Pick a localized string from a {tr,en,ar} dict with fallback chain.
  const pick = (obj, fallback = "") => {
    if (!obj) return fallback;
    if (typeof obj === "string") return obj;
    return obj[lang] || obj.en || obj.tr || Object.values(obj)[0] || fallback;
  };

  const t = (path) => {
    const parts = path.split(".");
    let cur = STRINGS[lang] || STRINGS.tr;
    for (const p of parts) {
      cur = cur?.[p];
      if (cur == null) break;
    }
    if (typeof cur === "string") return cur;
    // fallback to English
    let cur2 = STRINGS.en;
    for (const p of parts) cur2 = cur2?.[p];
    return typeof cur2 === "string" ? cur2 : path;
  };

  const value = useMemo(() => ({ lang, setLang, t, pick, isRtl: lang === "ar" }), [lang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
